import type { ClientOption } from '@fos/shared/contracts/crm';
import { useMemo } from 'react';
import { trpc } from '../../../lib/trpc';
import { Combobox, type ComboboxOption } from '../../../ui/Combobox';
import { ClientDot } from './ClientDot';

const NONE = '__none__';

/** Klienci do wyboru (zakres osoby, bez archiwum). Wspólny cache dla wszystkich pickerów na stronie. */
export function useClientOptions(opts: { includeArchived?: boolean; enabled?: boolean } = {}) {
  return trpc.clients.options.useQuery(
    { includeArchived: opts.includeArchived ?? false },
    { enabled: opts.enabled ?? true, staleTime: 60_000 },
  );
}

export interface ClientPickerProps {
  /** id klienta albo null (brak) */
  value: string | null;
  onChange: (clientId: string | null) => void;
  /** pozycja „Bez klienta” na początku listy (np. zadanie wewnętrzne) */
  allowNone?: boolean;
  noneLabel?: string;
  /** także klienci z archiwum (oznaczeni w opisie) */
  includeArchived?: boolean;
  /** bez klientów widocznych tylko jako karta (tryb częściowy), np. gdy wybór daje dostęp do kontaktów */
  excludePartial?: boolean;
  placeholder?: string;
  size?: 's' | 'm';
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

/**
 * Wybór klienta z wyszukiwaniem (Combobox + kropka koloru klienta). Do użycia w innych modułach (zadania, czas,
 * projekty, utrzymanie). Dane: `clients.options` (zakres osoby). W polu formularza: owiń w `<Field label="Klient">`.
 */
export function ClientPicker({
  value,
  onChange,
  allowNone = false,
  noneLabel = 'Bez klienta',
  includeArchived = false,
  excludePartial = false,
  placeholder = 'Wybierz klienta…',
  size = 'm',
  disabled,
  id,
  className,
  'aria-label': ariaLabel,
}: ClientPickerProps) {
  const query = useClientOptions({ includeArchived });
  const options = useMemo<ComboboxOption[]>(() => {
    const list: ClientOption[] = (query.data ?? []).filter((c) => !excludePartial || !c.partial);
    const out: ComboboxOption[] = list.map((c) => ({
      value: c.id,
      label: c.name,
      description: c.archived ? 'archiwum' : undefined,
      leading: <ClientDot color={c.color} />,
    }));
    if (allowNone) out.unshift({ value: NONE, label: noneLabel, leading: <ClientDot color={null} /> });
    return out;
  }, [query.data, excludePartial, allowNone, noneLabel]);

  return (
    <Combobox
      id={id}
      value={value ?? (allowNone ? NONE : null)}
      onValueChange={(v) => onChange(v === NONE ? null : v)}
      options={options}
      placeholder={query.isPending ? 'Wczytywanie…' : placeholder}
      searchPlaceholder="Szukaj klienta…"
      emptyText={query.isError ? 'Nie udało się wczytać klientów' : 'Brak klientów'}
      size={size}
      disabled={disabled}
      className={className}
      aria-label={ariaLabel}
    />
  );
}
